(() => {
  if (window.tnSizeSelectorReady) return;
  window.tnSizeSelectorReady = true;
  let controller;
  let currentUrl = location.href;
  // Shopify mounts Buy with Prime only once per document. A size swap replaces
  // that host, so reconnect its native component to the new checkout slot.
  function refreshShopPromise(html, signal) {
    const source = html.querySelector('#shop-promise-product');
    const current = document.querySelector('#shop-promise-product');
    if (!source || !current) return;
    current.textContent = source.textContent;
    const { variantId } = JSON.parse(source.textContent);
    const product = document.querySelector('product-info[id^="MainProduct"]');
    if (!variantId || !product) return;
    let observer;
    let timer;
    const cleanup = () => {
      observer?.disconnect();
      clearTimeout(timer);
      signal.removeEventListener('abort', cleanup);
    };
    const mount = () => {
      if (signal.aborted || !product.isConnected) { cleanup(); return true; }
      const slot = product.querySelector('[data-shopify="shop-promise-payment-button-slot"]');
      if (!slot || !customElements.get('delivery-promise-wc')) return false;
      let promise = product.querySelector('delivery-promise-wc');
      if (!promise) {
        promise = document.createElement('delivery-promise-wc');
        const generic = document.createElement('div');
        generic.slot = 'generic-promise';
        generic.id = 'generic-promise-wc';
        promise.append(generic);
      }
      promise.setAttribute('variant-id', variantId);
      promise.setAttribute('slot', 'promise');
      promise.setAttribute('data-strategy', 'dynamic-checkout-button-slot');
      promise.style.display = 'block';
      promise.removeAttribute('pre-mounted');
      if (promise.parentNode !== slot) slot.append(promise);
      cleanup();
      return true;
    };
    if (mount()) return;
    observer = new MutationObserver(mount);
    observer.observe(product, { childList: true, subtree: true });
    signal.addEventListener('abort', cleanup, { once: true });
    timer = setTimeout(cleanup, 10000);
    customElements.whenDefined('delivery-promise-wc').then(() => {
      if (!signal.aborted && product.isConnected) mount();
    });
  }
  async function switchSize(url, push = true) {
    controller?.abort();
    const request = controller = new AbortController();
    const status = document.querySelector('.tn-size-status');
    if (status) status.textContent = 'Loading your size…';
    document.body.classList.add('tn-size-loading');
    try {
      const response = await fetch(url, { signal: request.signal });
      if (!response.ok) throw new Error('Product unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const selectors = ['product-info[id^="MainProduct"]', '.tn-tub-nav', '.tn-highlight-size', '#tub-reviews', '.tn-size-comparison'];
      const pairs = selectors.map(selector => [document.querySelector(selector), html.querySelector(selector)]);
      if (pairs.some(pair => pair.some(node => !node))) throw new Error('Incomplete product page');
      const scrollY = window.scrollY;
      // Use Dawn's script-aware insertion for product forms and payment components.
      for (const [oldNode, source] of pairs) {
        const wrapper = document.createElement('div');
        // Theme custom elements are already registered; do not run their definitions twice.
        source.querySelectorAll('script[src]').forEach(script => {
          if ([...document.scripts].some(existing => existing.src === script.src)) script.remove();
        });
        HTMLUpdateUtility.setInnerHTML(wrapper, source.outerHTML);
        oldNode.replaceWith(wrapper.firstElementChild);
      }
      const sizeHeading = html.querySelector('.tn-highlight-size')?.closest('article')?.querySelector('h3');
      if (sizeHeading) document.querySelector('.tn-highlight-size').closest('article').querySelector('h3').textContent = sizeHeading.textContent;
      document.title = html.title;
      document.querySelector('link[rel="canonical"]')?.setAttribute('href', html.querySelector('link[rel="canonical"]')?.href || url);
      if (push) history.pushState({ tnSize: true }, '', url);
      currentUrl = url;
      refreshShopPromise(html, request.signal);
      window.Shopify?.PaymentButton?.init();
      // Judge.me legacy markup is server-rendered but needs its per-widget handlers.
      const legacy = document.querySelector('#tub-reviews .jdgm-legacy-widget-content');
      if (legacy && window.jdgm?.initializeWidgets) {
        legacy.style.display = '';
        const jm = window.jdgm;
        jm.caches.$revWidgets = jm.$('.jdgm-review-widget, [data-widget="review"]');
        jm.caches.$widgetWithReviews = jm.$('.jdgm-review-widget, .jdgm-revs-tab, .jdgm-all-reviews-widget');
        jm.caches.$prevBadges = jm.$('.jdgm-preview-badge');
        if (!jm._renderAndSetupReviewForm) {
          await new Promise((resolve, reject) => {
            const deadline = Date.now() + 8000;
            jm.loadScript(jm.CDN_HOST + 'widget/form.js');
            const check = () => {
              if (jm._renderAndSetupReviewForm) return resolve();
              if (Date.now() > deadline) return reject(new Error('Reviews did not initialize'));
              setTimeout(check, 50);
            };
            check();
          });
        }
        if (request.signal.aborted) return;
        jm.initializeWidgets();
        jm.customizeBadges();
      }
      window.Shopify?.PaymentButton?.init();
      window.ProductModel?.loadShopifyXR();
      document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: document.querySelector('product-info').dataset.section } }));
      window.scrollTo({ top: scrollY, behavior: 'instant' });
      document.querySelector('[data-tn-size][aria-current]')?.focus({ preventScroll: true });
      const nextStatus = document.querySelector('.tn-size-status');
      if (nextStatus) nextStatus.textContent = 'Size updated.';
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (!push) history.replaceState({}, '', currentUrl);
      console.error('Size switch failed', error);
      const errorStatus = document.querySelector('.tn-size-status');
      if (errorStatus) errorStatus.textContent = 'Couldn’t load that size. Please try again or open the size link in a new tab.';
    } finally {
      if (controller === request) document.body.classList.remove('tn-size-loading');
    }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest?.('[data-tn-size]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (!link.hasAttribute('aria-current')) switchSize(link.href);
  });
  window.addEventListener('popstate', () => {
    if (location.pathname !== new URL(currentUrl).pathname) switchSize(location.href, false);
  });
})();
